import { defineNuxtPlugin, useNuxtApp } from 'nuxt/app';
import type { Schema } from '~~/common/types/api';

type SignDisplayType = (
    | 'always'
    | 'auto'
);

interface IntlCacheEntry {
  formatPoints?: {
    [signDisplay in SignDisplayType]?: Intl.NumberFormat
  }
}

type PriceType = (
    | 'gross'
    | 'net'
    | 'grossDiscounted'
    | 'netDiscounted'
);


const intlCache: Record<string, IntlCacheEntry> = {};

function createPluginProvides () {
  const nuxtApp = useNuxtApp();
  const { $i18n } = nuxtApp;
  const priceFunction = function (priceValue: Partial<Schema<'PriceInterface'>>, currency: string, priceType: PriceType = 'gross', intlOptions: Intl.NumberFormatOptions = {}) {
    return price($i18n.locale.value, priceValue, currency, priceType, intlOptions);
  };

  const priceToNumberFunction = function (priceValue: string) {
    return priceToNumber($i18n.locale.value, priceValue);
  };

  const formatPointsFunction = function (opts: { number: number, pointChange?: boolean, locale?: string } = { number: 0 }) {
    return formatPoints(opts);
  };

  return {
    price: priceFunction,
    priceToNumber: priceToNumberFunction,
    formatPoints: formatPointsFunction,
  };
}

export type PricePluginProvides = ReturnType<typeof createPluginProvides>;

export default defineNuxtPlugin(() => {
  return {
    provide: createPluginProvides(),
  };
});

const forceHufFormat = (currency: string) => (currency === 'HUF') ? { minimumFractionDigits: 0, maximumFractionDigits: 0 } : {};

export function price (locale: string, priceValue: Partial<Schema<'PriceInterface'>>, currency: string, priceType: PriceType, intlOptions: Intl.NumberFormatOptions = {}) {
  const options: Intl.NumberFormatOptions = { style: 'currency', currency, ...forceHufFormat(currency), ...intlOptions };
  // Deliberately NaN and not 0 for a missing amount: rendering "0 Ft" would claim the item is free,
  // which is worse than a visibly broken price when the data is wrong.
  const value = priceValue[priceType] ?? Number.NaN;
  try {
    return new Intl.NumberFormat(locale, options).format(value);

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
  } catch (error) {
    return `${value}\xA0${currency}`;
  }
}

export function priceToNumber (locale: string, priceValue: string) {
  try {
    const intlSimple = new Intl.NumberFormat(locale);
    const DECIMAL = intlSimple.format(1.1).replace(/1/g, '');
    const REVERSED = Number(priceValue.match(new RegExp(`(\\d|\\${DECIMAL})+`, 'g'))?.join('')?.replace(DECIMAL, '.'));
    return REVERSED;


    // eslint-disable-next-line @typescript-eslint/no-unused-vars
  } catch (error) {
    return NaN;
  }
}

export function formatToLoyaltyPoints ({ number }: { number: number }) {
  const { $awt } = useNuxtApp();
  return `${formatPoints({ number })}\xA0${$awt('aw.global.loyalty_point_currency')}`;
}

export function formatPoints ({
  number,
  pointChange = false,
  locale = useNuxtApp().$i18n.locale.value,
}: {
  number: number,
  pointChange?: boolean,
  locale?: string,
}) {
  const signDisplay = pointChange ? 'always' : 'auto';
  if (!intlCache[locale]) {
    intlCache[locale] = {};
  }
  if (!intlCache[locale].formatPoints) {
    intlCache[locale].formatPoints = {};
  }
  if (!intlCache[locale].formatPoints[signDisplay]) {
    intlCache[locale].formatPoints[signDisplay] = new Intl.NumberFormat(
      locale,
      {
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
        signDisplay,
      },
    );
  }
  return intlCache[locale].formatPoints[signDisplay].format(number);
}
